/**
 * NN-Newton inverse design + scalar optimisation (DESIGN §8: NN-driven Newton inverse design).
 *
 * Two drivers over a `ParamSpec[]` design vector, both operating ONLY on the UNLOCKED DOFs
 * (mixed-inverse: lock every DOF outside the targeted surface region, the rest move):
 *
 *   - solveInverse  — least-squares target-Cp match. residual = cpMatchResidual(cpAt(vec), target,
 *     region); minimised by Levenberg-Marquardt (./lm) with box bounds taken from the specs. `cpAt`
 *     is injected (the NN surface-Cp forward in production; a synthetic map in tests) so this module
 *     needs no NN weights or geometry import and unit-tests on a closed-form invertible cpAt.
 *   - optimizeScalar — minimise an injected scalar objective(vec) over the unlocked DOFs with a
 *     bounded Nelder-Mead (a general scalar objective is not a sum of squares, so LM/Gauss-Newton
 *     is the wrong hammer; Nelder-Mead is derivative-free and box-projected).
 *
 * Both reconstruct the FULL design vector each evaluation by writing the packed unlocked values back
 * onto the specs (locked DOFs held at their value), then hand that to the injected map — so `cpAt` /
 * `objective` always see a length-`specs.length` vector in spec order.
 *
 * Pure: no NN, no geometry, no DOM, no module state. Injected-dependency by construction.
 */

import { lm, type LMOptions } from './lm';
import {
  type ParamSpec,
  packVector,
  unpackVector,
  unlockedIndices,
} from './paramSpec';
import { cpMatchResidual, type SurfaceCp } from './metrics';

/** Box bounds for the unlocked DOFs, in unlocked order (the layout of every packed vector). */
function unlockedBounds(specs: ParamSpec[]): { min: number[]; max: number[] } {
  const idx = unlockedIndices(specs);
  return { min: idx.map((i) => specs[i]!.min), max: idx.map((i) => specs[i]!.max) };
}

/** Reconstruct the full spec-order value vector from a packed (unlocked) vector, clamped to boxes. */
function fullVector(specs: ParamSpec[], packed: number[]): number[] {
  return unpackVector(specs, packed).map((s) => s.value);
}

export interface SolveInverseArgs {
  /** The design DOFs; only the unlocked ones are varied (mixed-inverse). */
  specs: ParamSpec[];
  /** Geometry→surface-Cp forward over the FULL design vector (spec order). NN-backed in production. */
  cpAt: (vec: number[]) => SurfaceCp;
  /** Target Cp distribution to match. */
  target: SurfaceCp;
  /** Optional [x0,x1] surface band to confine the match to (mixed-inverse over a region). */
  region?: [number, number];
  /** Levenberg-Marquardt options (maxIter, tol, lambda0, custom Jacobian). */
  opts?: LMOptions;
}

export interface SolveInverseResult {
  /** specs with unlocked values updated to the LM optimum (locked unchanged). */
  specs: ParamSpec[];
  /** Cost ½·‖r‖² after each accepted LM state, history[0] = initial cost. */
  history: number[];
  /** Final cost ½·‖r‖². */
  finalCost: number;
}

/**
 * Target-Cp inverse design. Builds the least-squares residual r(v) = cpMatchResidual(cpAt(full(v)),
 * target, region) over the UNLOCKED DOFs and runs LM with bounds from the specs. Drives the cost to
 * ~0 when the target is producible by the parametrisation (the headline MSES-parity feature). Pure —
 * `args.specs` is not mutated; the returned specs are a fresh array.
 */
export function solveInverse(args: SolveInverseArgs): SolveInverseResult {
  const { specs, cpAt, target, region, opts } = args;

  const residual = (packed: number[]): number[] =>
    cpMatchResidual(cpAt(fullVector(specs, packed)), target, region);

  // No unlocked DOFs: nothing to vary — report the residual at the current design.
  const v0 = packVector(specs);
  if (v0.length === 0) {
    const r = residual([]);
    let s = 0;
    for (const e of r) s += e * e;
    const c = 0.5 * s;
    return { specs: specs.map((s2) => ({ ...s2 })), history: [c], finalCost: c };
  }

  const result = lm(residual, v0, { bounds: unlockedBounds(specs), ...opts });
  return {
    specs: unpackVector(specs, result.v),
    history: result.history,
    finalCost: result.cost,
  };
}

export interface NelderMeadOptions {
  /** Maximum iterations (default 400). */
  maxIter?: number;
  /** Stop when the spread of simplex objective values falls below this (default 1e-10). */
  tol?: number;
  /** Initial simplex edge length in each coordinate (default 0.05·span, min 1e-3). */
  step?: number;
}

export interface OptimizeScalarArgs {
  /** The design DOFs; only the unlocked ones are varied. */
  specs: ParamSpec[];
  /** Scalar objective over the FULL design vector (spec order) to MINIMISE. */
  objective: (vec: number[]) => number;
  /** Nelder-Mead options. */
  opts?: NelderMeadOptions;
}

export interface OptimizeScalarResult {
  /** specs with unlocked values updated to the minimiser (locked unchanged). */
  specs: ParamSpec[];
  /** Best objective value after each iteration, history[0] = initial value. */
  history: number[];
  /** Final (minimum) objective value. */
  finalValue: number;
}

/** Clamp a packed (unlocked) vector into the unlocked boxes; returns a new array. */
function projectPacked(specs: ParamSpec[], packed: number[]): number[] {
  const idx = unlockedIndices(specs);
  return idx.map((i, k) => {
    const s = specs[i]!;
    const x = packed[k]!;
    return x < s.min ? s.min : x > s.max ? s.max : x;
  });
}

/**
 * Scalar-objective minimisation over the unlocked DOFs by a bounded (box-projected) Nelder-Mead
 * simplex. Every candidate vertex is clamped into the spec boxes before evaluation, so the optimum
 * is found inside the feasible region (and snapped to a bound when the unconstrained optimum lies
 * outside). Pure — `args.specs` is not mutated.
 */
export function optimizeScalar(args: OptimizeScalarArgs): OptimizeScalarResult {
  const { specs, objective, opts } = args;
  const f = (packed: number[]): number => objective(fullVector(specs, packed));

  const x0 = packVector(specs);
  const n = x0.length;

  // No unlocked DOFs: report the objective at the current design.
  if (n === 0) {
    const value = f([]);
    return { specs: specs.map((s) => ({ ...s })), history: [value], finalValue: value };
  }

  const idx = unlockedIndices(specs);
  const maxIter = opts?.maxIter ?? 400;
  const tol = opts?.tol ?? 1e-10;

  // Per-coordinate initial step: scaled to each box span (falls back to a small absolute step).
  const stepFor = (k: number): number => {
    if (opts?.step !== undefined && opts.step > 0) return opts.step;
    const s = specs[idx[k]!]!;
    return Math.max((s.max - s.min) * 0.05, 1e-3);
  };

  // Nelder-Mead coefficients (reflect, expand, contract, shrink).
  const alpha = 1,
    gamma = 2,
    rho = 0.5,
    sigma = 0.5;

  // Build the initial simplex: x0 plus a perturbation along each axis, all box-projected + evaluated.
  type Vertex = { x: number[]; fx: number };
  const verts: Vertex[] = [];
  const x0p = projectPacked(specs, x0);
  verts.push({ x: x0p, fx: f(x0p) });
  for (let k = 0; k < n; k++) {
    const x = x0p.slice();
    x[k] = x[k]! + stepFor(k);
    const xp = projectPacked(specs, x);
    verts.push({ x: xp, fx: f(xp) });
  }

  const history: number[] = [];
  const evalAt = (x: number[]): Vertex => {
    const xp = projectPacked(specs, x);
    return { x: xp, fx: f(xp) };
  };

  for (let iter = 0; iter < maxIter; iter++) {
    verts.sort((a, b) => a.fx - b.fx);
    history.push(verts[0]!.fx);

    const best = verts[0]!;
    const worst = verts[n]!;
    const secondWorst = verts[n - 1]!;

    // Convergence: simplex objective values have collapsed.
    if (Math.abs(worst.fx - best.fx) <= tol) break;

    // Centroid of all vertices except the worst.
    const centroid = new Array<number>(n).fill(0);
    for (let i = 0; i < n; i++) {
      const xi = verts[i]!.x;
      for (let j = 0; j < n; j++) centroid[j]! += xi[j]! / n;
    }

    // Reflection.
    const xr = centroid.map((c, j) => c + alpha * (c - worst.x[j]!));
    const refl = evalAt(xr);

    if (refl.fx < best.fx) {
      // Expansion.
      const xe = centroid.map((c, j) => c + gamma * (c - worst.x[j]!));
      const exp = evalAt(xe);
      verts[n] = exp.fx < refl.fx ? exp : refl;
    } else if (refl.fx < secondWorst.fx) {
      // Reflection accepted.
      verts[n] = refl;
    } else {
      // Contraction (outside if the reflection improved on the worst, else inside).
      const useOutside = refl.fx < worst.fx;
      const base = useOutside ? refl.x : worst.x;
      const xc = centroid.map((c, j) => c + rho * (base[j]! - c));
      const con = evalAt(xc);
      if (con.fx < (useOutside ? refl.fx : worst.fx)) {
        verts[n] = con;
      } else {
        // Shrink towards the best vertex.
        const b = best.x;
        for (let i = 1; i <= n; i++) {
          const xi = verts[i]!.x;
          const xs = b.map((bj, j) => bj + sigma * (xi[j]! - bj));
          verts[i] = evalAt(xs);
        }
      }
    }
  }

  verts.sort((a, b) => a.fx - b.fx);
  const bestV = verts[0]!;
  history.push(bestV.fx);
  return {
    specs: unpackVector(specs, bestV.x),
    history,
    finalValue: bestV.fx,
  };
}
